import { Box, Chip, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useState } from "react";
import { resolveMediaUrl } from "../../helper/resolveMediaUrl";

export function DetailModalShell({ isAr, icon, title, subtitle, onClose, children, maxWidth = 980 }) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "94%", sm: "90%", md: "88%" },
                maxWidth,
                maxHeight: "94vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.25,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    {icon}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={800} noWrap>
                            {title}
                        </Typography>
                        {subtitle ? (
                            <Typography variant="body2" sx={{ opacity: 0.88 }}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: "auto", flex: 1 }} className="none-view-scroll">
                {children}
            </Box>
        </Box>
    );
}

export function DetailMediaPanel({
    image,
    fallbackIcon,
    badges = [],
    title,
    subtitle,
    description,
}) {
    const theme = useTheme();
    const [imgError, setImgError] = useState(false);
    const imageUrl = resolveMediaUrl(image);
    const hasImage = Boolean(imageUrl) && !imgError;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
                gap: 2.5,
                mb: 3,
                p: 2.5,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
            }}
        >
            <Box
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    minHeight: { xs: 200, md: 220 },
                    bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#eef2ff",
                    border: "1px solid",
                    borderColor: theme.palette.mode === "dark" ? "divider" : "#dbeafe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {hasImage ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        onError={() => setImgError(true)}
                        sx={{ width: "100%", height: "100%", minHeight: { xs: 200, md: 220 }, objectFit: "cover" }}
                    />
                ) : (
                    <Box sx={{ textAlign: "center", color: "#64748b", p: 3 }}>
                        {fallbackIcon || <ImageOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8" }} />}
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                            {title}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                {badges.length ? (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
                        {badges.map((badge) => (
                            <Chip
                                key={badge.key || badge.label}
                                size="small"
                                label={badge.label}
                                color={badge.color || "default"}
                                variant={badge.variant || "filled"}
                                sx={badge.sx}
                            />
                        ))}
                    </Box>
                ) : null}
                <Typography variant="h5" fontWeight={900} sx={{ color: "#0D358C", overflowWrap: "anywhere" }}>
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontWeight: 600 }}>
                        {subtitle}
                    </Typography>
                ) : null}
                {description ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.7, overflowWrap: "anywhere" }}>
                        {description}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

export function DetailSection({ icon, title, children }) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                p: 2.25,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.background.paper,
                height: "100%",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
                <Box sx={{ color: "#0D358C", display: "flex" }}>{icon}</Box>
                <Typography fontWeight={800} sx={{ color: "#0D358C" }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Box>
    );
}

export function DetailInfoRow({ label, value, isLast = false }) {
    return (
        <Box sx={{ mb: isLast ? 0 : 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                {label}
            </Typography>
            <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
                {value || "—"}
            </Typography>
        </Box>
    );
}

export function DetailSummaryCard({ icon, label, value }) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.background.paper,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minHeight: 88,
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "#eff6ff",
                    color: "#0D358C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}
